import React, { memo } from 'react';

import type { AnnotationTypes } from '@atlaskit/adf-schema/annotation';
import type { EditorActions } from '@atlaskit/editor-core/actions';
import type {
	AnnotationState,
	InlineCommentCreateComponentProps,
	InlineCommentState,
	InlineCommentViewComponentProps,
} from '@atlaskit/editor-plugins/annotation';

import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getEditorAnnotationEventEmitter.ts';
import { CommentEditor } from '@confluence/comment/entry-points/LazyCommentEditor.ts';
import { getEditorResolvedState } from '@confluence/comments-util/entry-points/stateResolver.ts';
import { LoadableAfterPaint } from '@confluence/loadable';

import type { ViewComponentProps } from './ViewComponent.tsx';
import { ViewManager } from './ViewManager.tsx';
import type { CreateComponentProps } from './CreateComponent.tsx';
import { CreateManager } from './CreateManager.tsx';

const CreateComponent = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-CreateComponent" */ './CreateComponent.tsx'))
			.CreateComponent,
});

const ViewComponent = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-ViewComponent" */ './ViewComponent.tsx'))
			.ViewComponent,
});

const ViewComponentMemoized = ({
	pageId,
	editorActions,
}: {
	pageId: string;
	editorActions?: EditorActions;
}) =>
	memo((props: InlineCommentViewComponentProps) => {
		return <ViewComponent pageId={pageId} editorActions={editorActions} {...props} />;
	});

const CreateComponentMemoized = ({ pageId }: { pageId: string }) =>
	memo((props: InlineCommentCreateComponentProps) => {
		return <CreateComponent pageId={pageId} {...props} />;
	});

type GetEditorInlineCommentOptionsType = {
	pageId: string;
	editorActions: EditorActions;
	triggerAnalyticsEvent: any;
	updateUnresolvedInlineComment: (unresolvedInlineComments: string[]) => void;
	setEditorCreateProps: (
		createProps: (InlineCommentCreateComponentProps & CreateComponentProps) | null,
	) => void;
	setEditorViewProps: (
		viewProps: (InlineCommentViewComponentProps & ViewComponentProps) | null,
	) => void;
	editorAnnotationProviderManagersEnabled: boolean;
	getCanAddComments: () => boolean;
	contentType?: string;
};

type EditorInlineCommentOptions = {
	viewComponent: React.MemoExoticComponent<
		(viewComponentProps: InlineCommentViewComponentProps) => React.JSX.Element
	>;
	createComponent: React.MemoExoticComponent<
		(createComponentProps: InlineCommentCreateComponentProps) => React.JSX.Element
	>;
	getState: (annotations: any) => Promise<AnnotationState<AnnotationTypes, InlineCommentState>[]>;
	updateSubscriber: any;
	disallowOnWhitespace: boolean;
	isToolbarAbove: boolean;
	supportedBlockNodes: string[];
	onCommentButtonMount: () => void | ReturnType<typeof CommentEditor.preload>;
	getCanAddComments: () => boolean;
	contentType: string | undefined;
};

export const getEditorInlineCommentOptions = ({
	pageId,
	editorActions,
	triggerAnalyticsEvent,
	updateUnresolvedInlineComment,
	editorAnnotationProviderManagersEnabled,
	getCanAddComments,
	contentType,
}: GetEditorInlineCommentOptionsType): EditorInlineCommentOptions => {
	let updateSubscriber = getEditorAnnotationEventEmitter();
	if (!updateSubscriber?.off) {
		updateSubscriber = null;
	}

	return {
		viewComponent: editorAnnotationProviderManagersEnabled
			? ViewManager({ pageId, editorActions })
			: ViewComponentMemoized({ pageId, editorActions }),
		createComponent: editorAnnotationProviderManagersEnabled
			? CreateManager({ pageId })
			: CreateComponentMemoized({ pageId }),
		getState: (annotations: any): Promise<AnnotationState<AnnotationTypes, InlineCommentState>[]> =>
			getEditorResolvedState({
				annotations,
				pageId,
				triggerAnalyticsEvent,
				updateUnresolvedInlineComment,
			}),
		updateSubscriber,
		disallowOnWhitespace: true,
		isToolbarAbove: true,
		supportedBlockNodes: ['media'],
		onCommentButtonMount: (): void | ReturnType<typeof CommentEditor.preload> =>
			CommentEditor.preload(),
		getCanAddComments,
		contentType,
	};
};
